import type { CSSProperties, JSX } from 'react'
import type { AccountRow, ConfidenceLabel } from '../api.js'
import { T } from '../tokens.js'
import { characterizationOf, type EnrollmentStatus } from './Pill.js'

/**
 * The targeting list's filter rail, and the filter state itself.
 *
 * ## Why every one of these filters runs in the browser
 *
 * The plan's Task 12 table maps each section to a `GET /api/accounts` query
 * parameter and says none needs new server work. Two of the five cannot be
 * spelled that way, and once two are client-side the rest have to be:
 *
 * 1. **`confidence` and `enrollmentStatus` are single-valued on the wire.**
 *    The rail's Confidence and Enrollment sections are three independent
 *    toggles each — the mock's own controls, and the only reading under which
 *    the plan's mandated empty-state copy ("Every confidence band is switched
 *    off, so nothing can rank") can ever be true. Two bands on out of three is
 *    not a request this route accepts.
 * 2. **`total` would stop meaning one thing.** The route answers with the
 *    number of rows it returned, so a server-side industry filter would move
 *    the "eligible" figure in the page header every time a rep touched a
 *    select. Fetching the book once means `eligible` is the book and `shown`
 *    is what the rail left of it.
 * 3. **There is nothing to page through.** The route has no limit and no
 *    offset; the browser already holds every row the server would have
 *    filtered — 281 of them on the seeded book, in one 187KB response.
 *
 * **No filter here computes an estimate.** `sizeBand` arrives on the row,
 * computed server-side from the run's own stamped configuration; `confidence`
 * is the server's label; the sorts compare two numbers the server sent. The
 * SPA multiplies nothing, which is the rule the mock breaks.
 */

/** The two orders the mock's `Rank by` segmented control offers. */
export type SortKey = 'weighted' | 'raw'

export interface Industry {
  naics: string
  label: string
}

export interface FilterState {
  sort: SortKey
  /**
   * A free-text contains-search over the account's **name and number**, or `''`
   * for "no text filter".
   *
   * Held as the raw box contents rather than as a normalised needle, because it
   * is also the `<input>`'s value and a control that rewrites what a rep typed
   * while they are typing is a control that fights them. `matchesSearch` does
   * the normalising, once, at the point of comparison.
   */
  search: string
  /** Three independent toggles, not one selection. All three off is a legal, empty state. */
  confidence: Readonly<Record<ConfidenceLabel, boolean>>
  enrollment: Readonly<Record<EnrollmentStatus, boolean>>
  /** A NAICS code, or `null` for "any industry". */
  naics: string | null
  /** An index into `SIZE_BAND_EDGES_KW`, or `null` for "any peak demand". */
  sizeBand: number | null
  /**
   * "Not yet characterized" means **no approved characterization exists at
   * all** — `characterizationOf(row).state === 'none'`, which is what the
   * route's own `buildingType=null` filter selects. The mock's version of this
   * checkbox also keeps `undetermined` accounts, which collapses two of
   * display rule 3's three states into one: an account somebody reviewed and
   * could not classify is not an account nobody has looked at.
   */
  uncharacterizedOnly: boolean
}

/**
 * The book as it arrives: every band on, every industry, every size, ranked
 * confidence-weighted. `Reset to my book` restores exactly this, **including
 * `sort`** — the mock's reset leaves the sort where the rep put it, which
 * silently keeps half the state it claims to clear.
 */
export const DEFAULT_FILTERS: FilterState = Object.freeze({
  sort: 'weighted' as SortKey,
  search: '',
  confidence: Object.freeze({ high: true, medium: true, low: true }),
  enrollment: Object.freeze({ not_enrolled: true, enrolled: true, declined: true }),
  naics: null,
  sizeBand: null,
  uncharacterizedOnly: false,
})

/**
 * `config.cohort.sizeBandEdgesKw`, duplicated — because **no route exposes the
 * configuration document to a screen**. `GET /api/accounts/:id/derivation/config`
 * needs an account id and describes one estimate's stamped version; there is no
 * program-level config route at all.
 *
 * The duplication is deliberately confined to the *labels*. Filtering compares
 * `row.sizeBand`, which the server computed with `sizeBandOf` against the run's
 * own stamped edges, so a config change would make these four captions wrong
 * and would not make a single row land in the wrong band. Carried as a finding:
 * a config route deletes this constant.
 */
export const SIZE_BAND_EDGES_KW: readonly number[] = [250, 750, 1500]

/** `1500` as `1,500`. Hand-grouped for the reason every formatter here is — see `Pill.formatKw`. */
function groupInt(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export interface SizeBandOption {
  band: number
  label: string
}

/** Four bands from three edges, counted exactly as `sizeBandOf` counts them. */
export function sizeBandOptions(edges: readonly number[] = SIZE_BAND_EDGES_KW): SizeBandOption[] {
  const options: SizeBandOption[] = []
  for (let band = 0; band <= edges.length; band++) {
    const lower = edges[band - 1]
    const upper = edges[band]
    if (lower === undefined && upper !== undefined) options.push({ band, label: `Under ${groupInt(upper)} kW peak` })
    else if (lower !== undefined && upper !== undefined) {
      options.push({ band, label: `${groupInt(lower)} – ${groupInt(upper)} kW peak` })
    } else if (lower !== undefined) options.push({ band, label: `${groupInt(lower)} kW peak and above` })
  }
  return options
}

/**
 * The industries actually present in the book, not a fixed list.
 *
 * The mock's Industry control is a `<div>` reading `Any industry` with no
 * options anywhere in the file. Reading them off the response is what makes it
 * a control rather than a picture, and it also means the rail can never offer
 * an industry that would return nothing.
 *
 * Sorted with `<` rather than `localeCompare`, which reads the host's ICU
 * tables — the same reason nothing in this package calls `toLocaleString`.
 */
export function industriesOf(rows: readonly AccountRow[]): Industry[] {
  const byCode = new Map<string, string>()
  for (const row of rows) byCode.set(row.naics, row.naicsLabel)
  return [...byCode]
    .map(([naics, label]) => ({ naics, label }))
    .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0))
}

/** Total order over account id, ascending — the tie-break `query/accounts.ts` uses. */
function byAccountIdAsc(a: AccountRow, b: AccountRow): number {
  return a.accountId < b.accountId ? -1 : a.accountId > b.accountId ? 1 : 0
}

/**
 * The server's own `compareByRank`, re-stated over the rows it sent.
 *
 * It re-states rather than trusts the arrival order because the raw sort has to
 * be spelled somehow and a screen with two orders, one of which is "whatever
 * order the response happened to be in", is a screen whose second order is
 * untestable. Both comparators here compare two numbers the server computed.
 */
function byWeightedKw(a: AccountRow, b: AccountRow): number {
  if (a.curtailableKwWeighted !== b.curtailableKwWeighted) {
    return b.curtailableKwWeighted - a.curtailableKwWeighted
  }
  return byAccountIdAsc(a, b)
}

function byRawKw(a: AccountRow, b: AccountRow): number {
  if (a.curtailableKwRaw !== b.curtailableKwRaw) return b.curtailableKwRaw - a.curtailableKwRaw
  return byAccountIdAsc(a, b)
}

/**
 * The contains-search, and the four decisions in it.
 *
 * **1. Empty means no filter, not no rows.** `''` and `'   '` both answer
 * `true` for every row. A box a rep has not typed in cannot be a filter, and
 * one that emptied the list the moment they cleared it would read as a bug —
 * `Reset to my book` also relies on this, since it restores `search: ''`.
 *
 * **2. The needle is trimmed; the haystack is not tokenised.** Leading and
 * trailing whitespace is dropped, because it is what a paste from a spreadsheet
 * or a CRM field carries and no rep types it on purpose. Interior whitespace is
 * kept and is significant: this is one substring test, so `desert ridge` matches
 * and `desert  ridge` (two spaces) does not. It is not a word search and does
 * not pretend to be one — a fuzzy matcher that quietly reordered or split terms
 * would be a ranking this screen is not allowed to invent.
 *
 * **3. Case-insensitive through `toLowerCase`, never `toLocaleLowerCase`.**
 * Same rule `industriesOf` states about `localeCompare`: nothing in this package
 * reads the host's locale tables, so a rep in one browser and a rep in another
 * get the same list from the same letters.
 *
 * **4. The account NUMBER matches too.** The alternative was name-only, and it
 * was rejected: the number is rendered directly under the name in the same cell
 * of this very table, it is what a rep has in front of them off a bill, an email
 * or a CRM record, and pasting `4000002` into a box beside a list that visibly
 * contains `4000002` and getting an empty state is indistinguishable from a
 * broken filter. The union is safe to offer because the two fields barely
 * intersect — account numbers are digits and account names are not — so a rep
 * cannot get a surprising row out of the field they were not searching. The
 * label and the helper text both say `or number`, so it is an offer rather than
 * a coincidence.
 */
export function matchesSearch(row: AccountRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return row.name.toLowerCase().includes(needle)
    || row.accountNumber.toLowerCase().includes(needle)
}

/** Filter, then order. Never mutates the array it is given. */
export function applyFilters(rows: readonly AccountRow[], state: FilterState): AccountRow[] {
  const kept = rows.filter((row) => (
    state.confidence[row.confidence]
    && state.enrollment[row.enrollmentStatus]
    && (state.naics === null || row.naics === state.naics)
    && (state.sizeBand === null || row.sizeBand === state.sizeBand)
    && (!state.uncharacterizedOnly || characterizationOf(row).state === 'none')
    // Combinable with every one of the above, by being one more `&&` rather
    // than a pre-pass over `rows`. The list stays client-side either way: see
    // this file's header for why that is a recorded decision and not an
    // oversight — no `GET /api/accounts` parameter spells this, and a text
    // filter on the server would move the `eligible` count in the page header
    // on every keystroke.
    && matchesSearch(row, state.search)
  ))
  return kept.sort(state.sort === 'raw' ? byRawKw : byWeightedKw)
}

const section: CSSProperties = {
  padding: '16px 18px',
  borderBottom: `1px solid ${T.color.hairline}`,
}

const sectionLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const helperText: CSSProperties = {
  fontSize: 11.5,
  lineHeight: 1.45,
  color: T.color.muted,
  marginTop: 9,
}

const segmentBase: CSSProperties = {
  flex: 1,
  padding: '9px 14px',
  fontFamily: T.fontFamily,
  fontSize: 10.5,
  fontWeight: T.weight.bold,
  letterSpacing: '0.07em',
  textTransform: T.micro.transform,
  border: 0,
  borderRadius: T.radius.square,
  cursor: 'pointer',
  textAlign: 'left',
}

const chipBase: CSSProperties = {
  padding: '6px 13px',
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.07em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.pill,
  cursor: 'pointer',
}

const selectStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 9,
  padding: '10px 11px',
  border: `1.5px solid ${T.color.rule}`,
  borderRadius: T.radius.square,
  background: '#fff',
  color: T.color.ink,
  fontFamily: T.fontFamily,
  fontSize: 12.5,
}

function on(active: boolean, base: CSSProperties, outlined: boolean): CSSProperties {
  const border = outlined ? { border: `1.5px solid ${active ? T.color.navy : T.color.chipOff}` } : {}
  return active
    ? { ...base, ...border, background: T.color.navy, color: '#fff' }
    : { ...base, ...border, background: '#fff', color: outlined ? T.color.micro : T.color.body }
}

/**
 * The search box's label, and it is a real `<label htmlFor>` — not a
 * placeholder.
 *
 * A placeholder is not a label: it is gone the moment there is text in the box,
 * several screen readers do not announce it, and it is the lowest-contrast text
 * on the screen. Every other control in this rail has a real label for the same
 * reason the header of this file gives about painted `<div>` controls.
 */
export const SEARCH_LABEL = 'Find an account'
/** Says the number is searchable too, because otherwise nothing on screen does. */
export const SEARCH_HELPER
  = 'Matches any part of an account’s name or number. An empty box filters nothing out.'
export const RANK_HELPER
  = 'Weighted discounts kW by confidence, so a big uncertain account does not outrank a solid one.'
export const CHARACTERIZATION_HELPER
  = 'A filter, not a defect. Most accounts are uncharacterized and always will be.'
/**
 * The 32 accounts the rep will never find by switching `Declined` on, said out
 * loud.
 *
 * `GET /api/accounts` applies the program's decline-suppression window to the
 * default view (R-16 AC-3), so those rows are not in the response at all and no
 * rail control can bring them back. The `Declined` chip filters the enrollment
 * status of the rows that *did* arrive, which is a different thing, and a rep
 * who switches it on and sees nothing is owed the reason rather than left to
 * conclude the filter is broken. The window's length is configuration and no
 * route exposes it, so this text does not claim a number of days.
 */
export const SUPPRESSION_NOTE
  = 'Accounts declined recently are held back from this view by the program’s suppression window.'

/**
 * What is true instead when a saved list is applied.
 *
 * `routes/accounts.ts` switches the default cutoff off for `savedListId`, on
 * the stated grounds that a saved list is a membership a human chose rather
 * than a discovery view. So the note above stops being a fact and this one
 * starts being one; a rep looking at a list that contains a recent decline is
 * owed the reason it is visible here and nowhere else.
 */
export const LIST_SCOPE_NOTE
  = 'A saved list is shown in full, including any account that has recently declined.'

const CONFIDENCE_CHIPS: readonly { key: ConfidenceLabel; label: string }[] = [
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
]

const ENROLLMENT_CHIPS: readonly { key: EnrollmentStatus; label: string }[] = [
  { key: 'not_enrolled', label: 'Not enrolled' },
  { key: 'enrolled', label: 'Enrolled' },
  { key: 'declined', label: 'Declined' },
]

export interface FilterRailProps {
  state: FilterState
  industries: readonly Industry[]
  /**
   * Whether the program's decline-suppression window applies to what the table
   * is showing — i.e. whether `SUPPRESSION_NOTE` is currently *true*.
   *
   * It is not always. `GET /api/accounts` applies the cutoff to the default
   * view and deliberately does **not** apply it when `savedListId` is set
   * (`routes/accounts.ts`: "a saved list is a membership a human chose, not a
   * discovery view"). Leaving the note on screen while a saved list is applied
   * would state on a rep's screen that recent declines are being held back at
   * the exact moment they are not. Required rather than defaulted, so a future
   * caller has to answer the question rather than inherit an answer.
   */
  declineSuppression: boolean
  /**
   * An **updater**, not a value, and that is not a style preference.
   *
   * Every control here computes its next state from the current one, and
   * `onChange({ ...state, … })` reads `state` out of the render that drew the
   * control. Two changes inside one tick therefore both start from the same
   * snapshot and the first is lost. A human cannot click twice in one tick, so
   * this is invisible on screen — it showed up driving the page from the
   * console, where switching all three confidence bands off in a loop switched
   * off exactly one. Passing `setFilters` straight through means React queues
   * the updates instead.
   */
  onChange: (update: (previous: FilterState) => FilterState) => void
}

/**
 * Every control here is a real form control: a `<button>` carrying
 * `aria-pressed`, a `<select>` with a `<label>`, or a checkbox. Nothing is a
 * `<div>` painted to look like one, which is what three of the mock's five
 * sections are — and a painted control is not reachable by Tab, not announced,
 * and not operable by anyone who is not holding a mouse.
 */
export function FilterRail({ state, industries, declineSuppression, onChange }: FilterRailProps): JSX.Element {
  return (
    <div
      // Width and the rail's right-hand rule belong to the rail as a whole —
      // `TargetingList` draws them on the column that holds this and the saved
      // lists panel together, so the two panels read as one rail rather than as
      // two bordered boxes.
      style={{ background: '#fff' }}
    >
      {/*
        First in the rail, and first in the tab order after the saved lists.

        A rep with a name or a number in hand is not filtering a book — they are
        looking for one account — and making them scroll past five filter groups
        to get to the box is making the common errand the long one. It sits
        under the saved-lists panel for the reason the panel sits at the top of
        the rail: "which list am I looking at" is read before "what am I looking
        for in it", and this searches within the scope that panel sets.
      */}
      <div style={section}>
        <label htmlFor="filter-search" style={sectionLabel}>{SEARCH_LABEL}</label>
        <input
          id="filter-search"
          // `type="search"` rather than `text`: it is what the control is, and
          // it is what gets a browser's own clear affordance. `autoComplete` is
          // off because a browser offering a rep the account numbers they typed
          // on some other screen is offering them customer data from a form
          // history this app does not control.
          type="search"
          autoComplete="off"
          value={state.search}
          onChange={(event) => {
            const search = event.target.value
            onChange((previous) => ({ ...previous, search }))
          }}
          // The same box as the two `<select>`s below it, so the rail's three
          // full-width controls line up rather than nearly lining up.
          style={selectStyle}
        />
        <div style={helperText}>{SEARCH_HELPER}</div>
      </div>

      <div style={section}>
        <div id="rank-by-label" style={sectionLabel}>Rank by</div>
        <div
          role="group"
          aria-labelledby="rank-by-label"
          style={{ display: 'flex', marginTop: 9, border: `1.5px solid ${T.color.rule}` }}
        >
          <button
            type="button"
            aria-pressed={state.sort === 'weighted'}
            onClick={() => { onChange((previous) => ({ ...previous, sort: 'weighted' })) }}
            style={on(state.sort === 'weighted', segmentBase, false)}
          >
            Confidence-weighted
          </button>
          <button
            type="button"
            aria-pressed={state.sort === 'raw'}
            onClick={() => { onChange((previous) => ({ ...previous, sort: 'raw' })) }}
            style={on(state.sort === 'raw', segmentBase, false)}
          >
            Raw kW
          </button>
        </div>
        <div style={helperText}>{RANK_HELPER}</div>
      </div>

      <div style={section}>
        <div id="confidence-label" style={sectionLabel}>Confidence</div>
        <div
          role="group"
          aria-labelledby="confidence-label"
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}
        >
          {CONFIDENCE_CHIPS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-pressed={state.confidence[key]}
              onClick={() => {
                onChange((previous) => ({
                  ...previous,
                  confidence: { ...previous.confidence, [key]: !previous.confidence[key] },
                }))
              }}
              style={on(state.confidence[key], chipBase, true)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={section}>
        <div id="enrollment-label" style={sectionLabel}>Enrollment</div>
        <div
          role="group"
          aria-labelledby="enrollment-label"
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}
        >
          {ENROLLMENT_CHIPS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-pressed={state.enrollment[key]}
              onClick={() => {
                onChange((previous) => ({
                  ...previous,
                  enrollment: { ...previous.enrollment, [key]: !previous.enrollment[key] },
                }))
              }}
              style={on(state.enrollment[key], chipBase, true)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={helperText}>{declineSuppression ? SUPPRESSION_NOTE : LIST_SCOPE_NOTE}</div>
      </div>

      <div style={section}>
        <label htmlFor="filter-industry" style={sectionLabel}>Industry</label>
        <select
          id="filter-industry"
          value={state.naics ?? ''}
          onChange={(event) => {
            const naics = event.target.value === '' ? null : event.target.value
            onChange((previous) => ({ ...previous, naics }))
          }}
          style={selectStyle}
        >
          <option value="">Any industry</option>
          {industries.map((industry) => (
            <option key={industry.naics} value={industry.naics}>{industry.label}</option>
          ))}
        </select>

        <label htmlFor="filter-size-band" style={{ ...sectionLabel, display: 'block', marginTop: 16 }}>
          Size band
        </label>
        <select
          id="filter-size-band"
          value={state.sizeBand === null ? '' : String(state.sizeBand)}
          onChange={(event) => {
            const sizeBand = event.target.value === '' ? null : Number(event.target.value)
            onChange((previous) => ({ ...previous, sizeBand }))
          }}
          style={selectStyle}
        >
          <option value="">Any peak demand</option>
          {sizeBandOptions().map((option) => (
            <option key={option.band} value={String(option.band)}>{option.label}</option>
          ))}
        </select>
      </div>

      <div style={{ padding: '16px 18px' }}>
        <label style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={state.uncharacterizedOnly}
            onChange={() => { onChange((previous) => ({ ...previous, uncharacterizedOnly: !previous.uncharacterizedOnly })) }}
            style={{ width: 15, height: 15, accentColor: T.color.navy }}
          />
          <span>Not yet characterized</span>
        </label>
        <div style={helperText}>{CHARACTERIZATION_HELPER}</div>
      </div>
    </div>
  )
}
